import { COOKIES, FRONTEND_ROUTES } from '@/services/Constants';
import { PLAY_MODE_TYPES } from '@/shared/Constants';
import {
	getCookie,
	getLocalStorageItem,
	removeLocalStorageItem,
	setCookie,
	setLocalStorageItem,
} from '@/services/Helper';
import { historyReplace } from '@/services/History';

import type { Category } from '@/pages/freePlay/types';

/* left it for now, maybe logic will change
array should contain next object
{ name: 'ExperimentName', variantCount: 2, valid: true },
variantCount(2 - A/B test, 3 - A/B/C test, if valid is true FE will send experiment name in request headers)
export const experiments = []; */

interface ExperimentConfig {
	name?: string;
	random_variation_weights?: number[] | null;
}

interface LocalStorageEntry {
	key: string;
	value: string | null;
}

type ExperimentHeaders = Record<string, string>;

export const getVariant = (list: number[]) => {
	const totalWeight = list.reduce((acc, item) => acc + item, 0);
	const random = Math.floor(Math.random() * totalWeight);
	let variant = 0;
	let cumulativeWeight = 0;

	for (let i = 0; i < list.length; i++) {
		cumulativeWeight += list[i];

		if (random <= cumulativeWeight) {
			variant = i;
			break;
		}
	}

	return variant;
};

export const setupExperiment = (experimentName: string, randomVariationWeights: number[]) => {
	const variant = getVariant(randomVariationWeights);

	setLocalStorageItem(`X-Experiment-${experimentName}`, String(variant));
};

export const getAllLocalStorage = (): LocalStorageEntry[] => {
	return Object.keys(localStorage).reduce<LocalStorageEntry[]>((entries, key) => {
		entries.push({ key, value: localStorage.getItem(key) });
		return entries;
	}, []);
};

export const getRequestHeaderExperiments = (): ExperimentHeaders => {
	let headers: ExperimentHeaders = {};

	const allExperimentItems = getAllLocalStorage().filter((item) =>
		item.key.includes('X-Experiment'),
	);
	const experimentHeaders = allExperimentItems.reduce((result, item) => {
		if (item.value !== null) {
			result[item.key] = item.value;
		}
		return result;
	}, {} as ExperimentHeaders);
	headers = { ...headers, ...experimentHeaders };

	/* left it for now, maybe logic will change
	const validExperiments = experiments.filter((item) => item.valid);
	const invalidExperiments = experiments.filter((item) => !item.valid);
	if (validExperiments.length) {
		const experimentHeaders = validExperiments.reduce((o, key) => {
			if (getLocalStorageItem(key.name))
				return Object.assign(o, { [key.name]: +getLocalStorageItem(key.name) });
			return {};
		}, {});
		headers = { ...headers, ...experimentHeaders };
	}
	if (invalidExperiments.length) {
		invalidExperiments.map((item) => {
			if (getLocalStorageItem(item.name)) removeLocalStorageItem(item.name);
			return item;
		});
	} */

	return headers;
};

export const handleExperiment = (experiment?: ExperimentConfig | null) => {
	const { name, random_variation_weights: randomVariationWeights } = experiment || {};

	if (name && randomVariationWeights && !getLocalStorageItem(`X-Experiment-${name}`)) {
		setupExperiment(name, randomVariationWeights);
	}
};

export const deleteExperiment = (experimentName?: string) => {
	if (experimentName && getLocalStorageItem(`X-Experiment-${experimentName}`)) {
		removeLocalStorageItem(`X-Experiment-${experimentName}`);
	}
};

export const redirectToDailyGoal = () => {
	const wasRedirected = getCookie(COOKIES.redirectedToDailyGoal);
	if (!wasRedirected) {
		const oneDayExpiration = new Date(Date.now() + 24 * 60 * 60 * 1000);
		setCookie(COOKIES.redirectedToDailyGoal, true, {
			expires: new Date(oneDayExpiration),
		});
		historyReplace(FRONTEND_ROUTES.DAILY_GOAL);
	}
};

export const isLearningPath = (playMode?: string | null) => {
	const learningPathModes = [
		PLAY_MODE_TYPES.LEARNING_PATH,
		PLAY_MODE_TYPES.FREE_PLAY_AFTER_DAILY_GOAL,
	] as const;

	return !!playMode && learningPathModes.includes(playMode as (typeof learningPathModes)[number]);
};

export const isSpaceKey = (e: KeyboardEvent) => {
	return (
		(e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar' || e.key === 'Space') &&
		!e.repeat
	);
};

export const parseFreePlayCategories = (data: unknown): Category[] => {
	if (Array.isArray(data)) return data as Category[];
	return (data as { free_play_topic_categories: Category[] })?.free_play_topic_categories ?? [];
};

export const scrollContainerToBottom = (scrollContainer: HTMLElement | null, delay = 220) => {
	if (!scrollContainer) return;

	const scrollToBottom = () => {
		try {
			scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
		} catch (e) {
			// Fallback for environments without scrollTo options support
			scrollContainer.scrollTop = scrollContainer.scrollHeight;
		}
	};

	// Scroll immediately and then after the animation completes
	scrollToBottom();
	setTimeout(scrollToBottom, delay);
};
